import { getUserDataPath } from '@mimir/web/desktop/utils';
import EventBus, { EVENTS, TOAST, showToast } from '@mimir/web/eventBus';
import type { UploadSummary } from '@mimir/client-common/upload/UploadItem';
import { UploadItem } from '@mimir/client-common/upload/UploadItem';
import type { LayoutFeatures } from '@mimir/web/model/layoutFeatures';
import type { User, UserRole } from '@mimir/web/model/User';
import type { PersonResource } from '@mimir/web/pages/person/person-interfaces';
import { getStorageByIdFromState, downloadWithoutNavigationWarning } from '@mimir/web/store/storeMisc';
import {
  download,
  filterOutMatchingPersonImages,
  getCloudLiveMediaBlob,
  getDownloadLiveMediaNotifier,
  getEXIFEmbeddedImageURL,
} from '@mimir/web/util/misc';
import { MAX_IMAGE_SIZE_FOR_EXIF_EMBEDDING_BYTES } from '@mimir/common/util/constants';
import { goToFolder } from '@mimir/web/util/folder';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { getKeldaStatuses } from '@mimir/web/api/globalClientKelda';
import {
  createItemUploadLock,
  deleteItemUploadLock,
  getItem,
  getItemFields,
  getItemUploadLock,
  getItemVersionsHistory,
  getLiveSourcesV2,
  getOptionList,
  getOptionListIds,
  getRouters,
  getTenantConfigDefaultUploadLocation,
  getTenantStorages,
  reprocessItem,
  undeleteItem,
} from '@mimir/web/util/api';
import {
  isSuperAdmin,
  isSupportTeam,
  isTenantAdmin,
} from '@mimir/web/util/security';
import type { UserInterfaceSettings } from '@mimir/web/util/localStorage';
import { getDefaultStorage, loadSortParams, saveSortParams, setDefaultStorage, setUISettings } from '@mimir/web/util/localStorage';
import { getEmbed } from '@mimir/common/client/hal';
import { CancelTokenManager } from '@mimir/client-common/util/subclip';
import { combineAll } from '@mimir/common/async/combineAll';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import { runTask } from '@mimir/common/async/taskScope';
import { AsyncOperationState, ReprocessActionType } from '@mimir/common/dto/asyncOperationPrimitives';
import type { AsyncOperationType } from '@mimir/common/dto/asyncOperationType';
import type { SearchResultHit, Item } from '@mimir/common/dto/searchResultHit';
import type {
  StandardItemEntryDto
} from '@mimir/common/dto/itemModels';
import {
  notGettableItemTypes
} from '@mimir/common/dto/itemModels';
import { ItemState } from '@mimir/common/dto/itemState';
import type { DynamoItemType } from '@mimir/common/dto/itemTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { FileSetProgress, FileSetListener } from '@mimir/common/upload/uploadProgression';
import type { KeldaConfigDto } from '@mimir/common/dto/keldaConfigDto';
import type { LiveSourceDtoV2 } from '@mimir/common/dto/liveSourceDtos';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { PersonDto } from '@mimir/common/dto/personModel';
import { isInstanceOfPersonDto } from '@mimir/common/dto/personModel';
import type {
  SearchDateOption,
  SortParameters
} from '@mimir/common/dto/searchModels';
import {
  filterTypeForKey,
} from '@mimir/common/dto/searchModels';
import type { StorageType } from '@mimir/common/dto/storageModel';
import type { ArchiveConfig, TenantConfigDto } from '@mimir/common/dto/tenantModel';
import type { CreateItemUploadLockRequestDto, GetItemUploadLockResponseDto, UploadLocationClientDto } from '@mimir/common/dto/uploadLocationDto';
import type {
  Filter
} from '@mimir/common/filters/Filters';
import {
  checkIfValidDateSearchExceptAnyOrCustom,
  clearFilters,
  combineFilters,
  Filters,
  isDefault,
  mapFilters,
} from '@mimir/common/filters/Filters';
import type { ParsedFilter } from '@mimir/common/filters/filter-util';
import { supportsSameTime } from '@mimir/common/filters/filter-util';
import type { TimedCoordinate } from '@mimir/common/dto/GeoLocation';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { DEFAULT_MDF_ID, SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import type { FileApi } from '@mimir/common/upload/fileApi';
import { isFileApi } from '@mimir/common/upload/fileApi';
import { UploadState } from '@mimir/common/upload/upload';
import { updateUploadItemId, trackUploadComplete } from '../util/sentryUploadTracking';
import {
  doesItemOnlyHaveProxy,
  getInitialPlayStartTime,
} from '@mimir/common/util/item';
import { OperationApiContext } from '@mimir/common/util/operationApiContext';
import { createUuid } from '@mimir/common/util/createUuid';
import { invariant } from '@mimir/common/util/invariant';
import type { VueDeleteValue, VueSetValue } from '@mimir/common/util/general';
import { isValidGUID } from '@mimir/common/util/general';
import { canPerformOwnTenantSupport, hasTenantInfoAccess } from '@mimir/common/util/security';
import type { Canceler } from 'axios';
import Axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import noop from 'lodash/noop';
import sortBy from 'lodash/sortBy';
import startCase from 'lodash/startCase';
import union from 'lodash/union';
import Vue from 'vue';
import type { Store, StoreOptions } from 'vuex';
/*VUE2*/ import Vuex from 'vuex';
/*VUE3*/ import { createStore } from 'vuex-next';
import { getGlobalRouter } from '@mimir/web/router';
import { configModule } from './config-module';
import { desktopModule, storeDispatchStorageProfileChanged } from './desktop-module';
import { exportModule } from './export-module';
import * as initialValues from './initialValues';
import type { SearchState } from './interfaces';
import { mdfModule } from './mdf-module';
import * as mutations from './mutations';
import { searchModule } from './search-module';
import { timelineModule } from './timeline-module';
import type { ItemAttachmentUploadState, PosterFrameProcessingType } from '@mimir/client-common/model/itemAttachment';
import { getOngoingAttachmentUploadKey } from '@mimir/client-common/model/itemAttachment';
import type { UserInfoDto } from '@mimir/common/dto/userDto';
import { getUserDisplayName } from '@mimir/common/dto/userDto';
import { isKeldaLiveItem } from '@mimir/common/util/kelda';
import type { MosItemReplaceData } from '@mimir/common/mos/mos-interfaces';
import { VersionState } from '@mimir/common/dto/itemVersionInfo';
import type { RouterDto } from '@mimir/common/dto/routerControllerDtos';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import { ViewType } from '@mimir/web/pages/search/constants';
import type { SetItemEditStatusMutationPayload } from '../util/itemEditStatusTypes';
import { getActiveStorageFolders } from '@mimir/web/util/kelda';
import { getVersionsSortedByCreatedDateDesc } from './version-sort';
import type { State } from './state';
import { PlayoutDevicesHelper } from '../pages/playout/playoutDevicesHelper';
import { generateRouterLink } from '../util/route';
import { handleLoadingScreenRemoval } from '../splash/handleLoadingScreenRemoval';
import { getGlobalServiceDocument, globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { handleMdfUpdatesToItem } from '@mimir/common/mdf/update';
import { userSettings } from '@mimir/web/globals/userSettings';
import { showPleaseWaitDialog, hidePleaseWaitDialog } from '@mimir/web/dialogs/pleaseWait';
import { showSimpleAsyncDialog } from '../dialogs/simpleAsync';
import { getI18n } from '@mimir/common/i18n/initializeI18n';
import { startPollingTransferProgress } from '../globals/transferProgress';
import type { QuickPreviewTabName } from '@mimir/common/types/UIOnlySections';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import type { TimedMetadataItemModel } from './timed-mdf-module-state';
import { isInElectron, type ApplicationContext, type ElectronContext, isInPremiere } from '@mimir/common/context/applicationContext';
import { DefaultNotificationOps } from '../premiere/defaultNotificationOps';
import { ConsoleLogger } from '@mimir/common/util/logging';
import { DefaultOngoingDownloadOps } from '../premiere/defaultOngoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import type { SetDateFilterFieldPayload } from '@mimir/web/util/filterUtils';
import { resetDateFilterIfCustom } from '../pages/search/util';
import { appState, getGlobalStore, getSearchPageVisible, pollSearchHitsIfNeeded, setGlobalStore, storeDispatchInsertUploadItemIntoSearch } from '.';
import type { AmeConfig } from '../desktop/ame/config';
import { launchWithConfig as launchAmeWithConfig } from '../desktop/ame/launch-step';
import type { DvrConfig } from '../desktop/dvr/config';
import { launchWithConfig as launchDvrWithConfig } from '../desktop/dvr/launch-step';
import { getAnalyticConfig } from '../util/analytics';
import { folders, setFolderIdToPreview } from '../globals/folders';
import { ApiActions } from '@mimir/client-common/util/apiActions';
import type { LoadedItem } from '@mimir/common/dto/searchResultHit';
import type {
  ConsecutiveSearchItemSelectionPayload,
  FetchVersionsForSearchPayload,
  LoadCurrentItemMetadataPayload,
  LoadItemMetadataPayload,
  LoadItemMetadataResult,
  OpenItemByIdPayload,
  OpenItemPayload,
  StoreDoUploadPayload,
  ToggleSearchItemSelectionPayload,
  UploadItemFilesPayload
} from './storePayloads';
import type { QuickPreview } from '../model/quickPreview';
import { storeDispatchLoadItemMetadata, storeDispatchSingleItemSearch, storeDispatchUpdateSearchHitWithMedia } from './storeDispatch';

invariant(initialValues.sideBarItems, 'Require initialValues.sideBarItems to be initialized');

/*
   the use of 'any' here is a workaround for a paradox defined here: https://github.com/vuejs/vuex/issues/1652
   Essentially, VueX does not want you to declare modules as part of the initial rootState, and will warn about this on application load
   in the console, if violated. However, if you want to access module state from the root state, typescript will complain since
   it is sensing you are accessing properties that may not exist. By declaring the initialState object as any, and later
   casting it to a proper interface declaration of our State, we are able to get rid of declaring our modules in the rootState while still
   retaining full type safety when using root state.
*/
const initialState: any = {
  appLoaded: false,
  wsConnected: false,
  mosItemReplaceData: null,
  backToExport: false,
  isLoadingUsers: false,
  sideBarItems: initialValues.sideBarItems,
  supportedOps: initialValues.supportedOps,
  userInterfaceSettings: {},
  schedulerPageSettings: {},
  ongoingUploads: [],
  itemIdsToPoll: [],
  selectedUpload: null,
  expectedItemType: null,
  selectedFiles: [],
  user: initialValues.loggedInUser,
  allUsers: [],
  filters: new Filters(new ConsoleLogger(), appState.dateHelper),
  serviceLinks: {},
  clientConfig: initialValues.clientConfig,
  storage: initialValues.storage,
  liveSources: [],
  routers: [],
  optionLists: [],
  ongoingFetchOfOptionListIds: {},
  searchMap: {
    zoom: 2,
    center: { lat: 60.3929, lng: 5.3241 },
    isInitialMap: true,
  },
  itemUploadLock: {},
  uploadSummary: null,
  loadedUploadLocation: null,
  preferredUploadStorageKey: null,
  freshUploadLocation: false,
  currentPersonName: '',
  preventBrowserNavigationWarning: true,
  desktopProfileMenuName: '',
  postMessageOrigin: '*',
  layoutFeatures: {},
  latestLogId: '',
  thisClientInstanceId: createUuid(),
  posterFrameProcessingState: 'none',
  ongoingItemAttachmentUploads: {},
  experimental: false,
  dialogCounter: 0,
  forceMouseCursor: null,
  copiedItemId: null,
  itemEditStatusMap: {},
  notifiedHostingFrame: false,
  enableRichPayloads: false,
  playoutDevicesHelper: new PlayoutDevicesHelper(),
  lastPolledItems: [],
  enableUpdatingMimir: false,
  lastCopyLinkItemId: null as string | null,
  storagesLoaded: false,
};

/**
 * Fired when the app is done loading.
 */
const appLoaded = (state: State) => {
  handleLoadingScreenRemoval();
  state.appLoaded = true;
};

const isValidNavigationDrawerSize = (w: number) => {
  if (w < 200 || w > window.innerWidth / 2) return false;
  return true;
};

const updateSupportedOps = (state: State) => {
  const supportedOps: AsyncOperationType[] = [];
  for (const op of initialValues.supportedOps) {
    if (!state.config.tenantConfig.showPersonFunctionality && op === 'personDetection') continue;
    if (!state.config.tenantConfig.showPersonFunctionality && op === 'celebrityDetection') continue;
    if (!state.config.tenantConfig.showLabelFunctionality && op === 'labelDetection') continue;
    supportedOps.push(op);
  }
  state.supportedOps = supportedOps;
};

/**
 * Any side-effects that changed config settings have should be performed here.
 */
const updateFromTenantConfigSettings = (state: State, updatedConfig: TenantConfigDto) => {
  state.filters.defaultFilters['filter:person'].isHidden = !updatedConfig.showPersonFunctionality;
  state.filters.defaultFilters['filter:label'].isHidden = !updatedConfig.showLabelFunctionality;
  state.filters.defaultFilters['filter:pinned'].isHidden = !updatedConfig.featureFlags.pinning;
  state.sideBarItems.Folders.isVisible = updatedConfig.showFolderFunctionality;
  updateSupportedOps(state);
};

/**
 * Any state change that is based on the user roles should be done here.
 */
const updateUserRoles = (state: State, roles: UserRole) => {
  state.user.roles = roles;
  updateSupportedOps(state);
};

const createStoreFn = (options: StoreOptions<State>) => {
  if (import.meta.env.VUE_APP_V2) return new Vuex.Store(options);
  else return createStore(options);
};

export function initializeGlobalStore(): Store<State> {
  const store_ = createStoreFn({
    state: initialState as State,
    modules: {
      mdf: mdfModule,
      desktop: desktopModule,
      timeline: timelineModule,
      search: searchModule,
      export: exportModule,
      config: configModule,
    },
    mutations: {
      notifiedHostingFrame: state => state.notifiedHostingFrame = true,
      enableRichPayloads: state => state.enableRichPayloads = true,
      clearMouseCursor: state => state.forceMouseCursor = null,
      forceMouseCursorTo: (state, val: string) => state.forceMouseCursor = val,
      setUsers: (state, val: UserInfoDto[]) => state.allUsers = sortBy(val, user => getUserDisplayName(user)),
      injectFakeAvatar: (state, imgBlobUrl: string) => {
        Vue.set(state.user, 'avatar', imgBlobUrl);
        Vue.set(state.user, 'highResAvatar', imgBlobUrl);
        const user = state.allUsers.find(u => u.id === state.user.id);
        if (user) {
          Vue.set(user, 'avatar', imgBlobUrl);
        }
      },
      clearAvatar: state => {
        Vue.delete(state.user, 'avatar');
        Vue.delete(state.user, 'highResAvatar');
        const user = state.allUsers.find(u => u.id === state.user.id);
        if (user) {
          Vue.delete(user, 'avatar');
        }
      },
      setWsConnected: (state, val: boolean) => state.wsConnected = val,
      setLatestLogId: (state, val: string) => state.latestLogId = val,
      setPostMessageOrigin: (state, val: string) => state.postMessageOrigin = val,
      setMosItemReplaceData: (state, val: MosItemReplaceData) => state.mosItemReplaceData = val,
      preventBrowserNavigationWarning: (state, val: boolean) => state.preventBrowserNavigationWarning = val,
      addItemsToPoll: (state, itemIds: string[]) => {
        state.itemIdsToPoll = union(itemIds, state.itemIdsToPoll);
      },
      setItemsToPoll: (state, itemIds: string[]) => {
        state.itemIdsToPoll = itemIds;
      },
      setLayoutFeatures: (state, payload: LayoutFeatures) => Vue.set(state, 'layoutFeatures', payload),
      backToExport: (state, payload: boolean) => state.backToExport = payload,
      setExpectedItemType: (state, payload: DynamoItemType | null) => state.expectedItemType = payload,
      selectedFiles: (state, payload: File[]) => state.selectedFiles = payload,
      appLoaded: state => appLoaded(state),
      setPremiereSidebarMenuVisible: (state, payload: boolean) => {
        state.sideBarItems.Premiere.isVisible = payload;
      },
      setDvrSidebarMenuVisible: (state, payload: boolean) => {
        state.sideBarItems.DVR.isVisible = payload;
      },
      setDateFilterField: (state, payload: SetDateFilterFieldPayload | undefined) => {
        if (!payload) {
          return;
        }
        state.filters.setDateFilter({...payload, dateHelper: appState.dateHelper});
      },
      removeAllFromOngoingUploads: state => (state.ongoingUploads = []),
      updateGeoShapeFilter: (state, payload?) => {
        const geoFilter = state.filters.defaultFilters['filter:geoLocation'];
        if (payload)
          geoFilter.setValue(payload);
        else
          geoFilter.clearValue();
      },
      setFolderId: (state, id: string) => {
        state.filters.defaultFilters['filter:folderId'].setValue([id]);
      },
      clearAllFilters: (state, keepFolderId = false) => {
        clearFilters({ filters: state.filters, userDefaultFilters: userSettings.defaultFilters, keepFolderId });
      },
      clearCustom: state => {
        // Only clear custom fields coming from custom mdf - not common custom fields.
        for (const f in state.filters.customFilters) {
          const filter = state.filters.customFilters[f];
          if (filter && filter.metadataFormId !== DEFAULT_MDF_ID)
            Vue.delete(state.filters.customFilters, f);
        }

        state.filters.defaultFilters['filter:formId'].clearValue();
      },
      updateFilters: (state, parsedFilters: ParsedFilter[]) => {
        /**
         * If no filters are found in the search string, we clear the filters state.
         */
        if (isEmpty(parsedFilters)) {
          clearFilters({ filters: state.filters, userDefaultFilters: userSettings.defaultFilters });
          // We should signal the date filter to zoom out regardless when all filters are being cleared.
          EventBus.emit(EVENTS.RESET_DATEFILTER);
          return;
        }

        // Get all active date filters (both default and custom)
        const allActiveDateFilters = state.filters.getActiveDateFilters();

        // Check if any of the active date filters exist in parsed filters
        const hasDateFilterInParsed = parsedFilters.some(pf => {
          if (!pf.isRecognized) return false;
          // Check against all active date filter keys AND readableKeys
          return allActiveDateFilters.some(df =>
            df.key === pf.token || (df.readableKey && df.readableKey === pf.token));
        });

        // Only emit RESET if NO date filters exist in parsed filters
        if (!hasDateFilterInParsed) {
          EventBus.emit(EVENTS.RESET_DATEFILTER);
        } else {
          // Check if default date filter has a valid SearchDateOption
          const defaultDateFilter = parsedFilters.find(f => f.isRecognized && f.token === state.filters.dateFilter.key);
          if (defaultDateFilter && checkIfValidDateSearchExceptAnyOrCustom(defaultDateFilter.value as SearchDateOption)) {
            state.search.searchForm.dateTimeSearch = defaultDateFilter.value as SearchDateOption;
          }
        }

        mapFilters(new ConsoleLogger(), state.filters, parsedFilters);
      },
      replaceFilters: (state, val: Filters) => {
        state.filters = val;
      },
      resetSearchMap: state => {
        state.searchMap.isInitialMap = true;
        state.searchMap.zoom = 2;
        state.searchMap.center = { lat:0, lng: 0};
      },
      setThirdColumn: (state, payload: QuickPreviewTabName | null) => Vue.set(state.userInterfaceSettings, 'thirdColumn', payload),
      setBookmarkMenuOpened: (state, payload: boolean) => Vue.set(state.userInterfaceSettings, 'bookmarkMenuOpened', payload),
      setBookmarksOpened: (state, payload: string[]) => Vue.set(state.userInterfaceSettings, 'bookmarksOpened', payload),
      rememberFolderTreeActive: (state, payload: boolean) => Vue.set(state.userInterfaceSettings, 'isFolderTreeActive', payload),
      setUISettings: (state, payload: UserInterfaceSettings) => (state.userInterfaceSettings = payload),
      setSearchMenuExpandState: (state, payload: boolean) => Vue.set(state.userInterfaceSettings, 'searchMenuExpandState', payload),
      showPlayoutDevices: (state, payload: boolean) => Vue.set(state.userInterfaceSettings, 'showPlayoutDevices', payload),
      setDrawerVisibility: (state, payload: boolean) => Vue.set(state.userInterfaceSettings, 'drawerVisible', payload),
      setDrawerWidth: (state, payload: number) => {
        if (isValidNavigationDrawerSize(payload)) {
          Vue.set(state.userInterfaceSettings, 'drawerWidth', payload);
        }
      },
      setSearchMapZoom: (state, zoom: number) => (state.searchMap.zoom = zoom),
      setSearchMapCenter: (state, center: TimedCoordinate) => (state.searchMap.center = center),
      setSearchMapInitialState: (state, value: boolean) => (state.searchMap.isInitialMap = value),
      setUser: (state, payload: User) => state.user = payload,
      setMfaPreference: (state, payload: boolean) => state.user.isSoftwareTokenMfaEnabled = payload,
      tenantConfigUpdated: (state, payload: TenantConfigDto) => updateFromTenantConfigSettings(state, payload),
      pushToOngoingUploads: (state, value: UploadItem) => state.ongoingUploads.push(value),
      removeFromOngoingUploads: (state, value: UploadItem) => {
        const index = state.ongoingUploads.indexOf(value);
        if (index >= 0) {
          state.ongoingUploads.splice(index, 1);
        }
      },
      clearOngoingUploads: state => state.ongoingUploads = [],
      clearAllUploadsExceptErrored: state => state.ongoingUploads = state.ongoingUploads.filter(item => item.uploadState === UploadState.ERROR),
      updatePersonInResultSet: (state, person: PersonResource) => mutations.updatePersonInResultSet(state, person),
      storages: (state, payload: StorageType[]) => (state.storage.all = payload),
      defaultStorage: (state, value: StorageType | undefined) => (state.storage.default = value),
      updateStorage: (state, updatedStorage: StorageType) => mutations.updateStorage(state, updatedStorage),
      setUserRoles: (state, roles: UserRole) => updateUserRoles(state, roles),
      setSelectedUpload: (state, item: UploadItem | null) => state.selectedUpload = item,
      setItemUploadLock: (state, itemUploadLock: GetItemUploadLockResponseDto) => state.itemUploadLock = itemUploadLock,
      addStorage: (state, storage: StorageType) => state.storage.all.push(storage),
      removeStorage: (state, storage: StorageType) => {
        const index = state.storage.all.findIndex(s => s.id === storage.id);
        if (index >= 0) {
          state.storage.all.splice(index, 1);
        }
      },
      liveSources: (state, payload: LiveSourceDtoV2[]) => (state.liveSources = payload),
      updateLiveSource: (state, updated: LiveSourceDtoV2) => mutations.updateLiveSource(state, updated),
      addLiveSource: (state, source: LiveSourceDtoV2) => state.liveSources.push(source),
      removeLiveSource: (state, source: LiveSourceDtoV2) => {
        const index = state.liveSources.findIndex(s => s.id === source.id);
        if (index >= 0) {
          state.liveSources.splice(index, 1);
        }
      },
      routers: (state, payload: RouterDto[]) => (state.routers = payload),
      updateRouter: (state, updated: RouterDto) => mutations.updateRouter(state, updated),
      addRouter: (state, r: RouterDto) => state.routers.push(r),
      removeRouter: (state, r: RouterDto) => {
        const index = state.routers.findIndex(s => s.id === r.id);
        if (index >= 0) {
          state.routers.splice(index, 1);
        }
      },
      optionLists: (state, payload: OptionListDto[]) => (state.optionLists = payload),
      updateOptionList: (state, updated: OptionListDto) => mutations.updateOptionList(state, updated),
      addOptionList: (state, l: OptionListDto) => {
        const ind = state.optionLists.findIndex(ol => ol.id === l.id);
        if (ind >= 0) {
          state.optionLists[ind] = l;
          return;
        }
        state.optionLists.push(l);
      },
      removeOptionList: (state, toRemove: OptionListDto) => {
        const index = state.optionLists.findIndex(l => l.id === toRemove.id);
        if (index >= 0) {
          state.optionLists.splice(index, 1);
        }
      },
      setUploadSummary: (state, uploadSummary: UploadSummary | null) => state.uploadSummary = uploadSummary,
      setLoadedUploadLocation: (state, uploadLocation: UploadLocationClientDto) => {
        state.loadedUploadLocation = uploadLocation;
        state.freshUploadLocation = true;
      },
      setPreferredUploadStorageKey: (state, storageKey: string | null) => {
        state.preferredUploadStorageKey = storageKey;
      },
      staleUploadLocation: (state) => {
        state.freshUploadLocation = false;
      },
      setPersonName: (state, personName: string) => state.currentPersonName = personName,
      desktopProfileMenuName: (state, name: string) => state.desktopProfileMenuName = name,
      setColdStorageConfig: (state, archiveConfig: ArchiveConfig) => Vue.set(state.config.tenantConfig, 'archiveConfig', archiveConfig),
      setPlayerVolumeInSettings: (state, playerVolume: number) => state.userInterfaceSettings.playerVolume = playerVolume,
      setIsMutedInSettings: (state, payload: boolean) => state.userInterfaceSettings.isMuted = payload,
      setShouldShowDateFilter: (state, payload: boolean) => Vue.set(state.userInterfaceSettings, 'showDateFilter', payload),
      setIncludeSubfolders: (state, payload: boolean) => Vue.set(state.userInterfaceSettings, 'includeSubfolders', payload),
      setSearchThumbnailSize: (state, payload: number) => Vue.set(state.userInterfaceSettings, 'searchThumbnailSize', payload),
      updateOngoingItemAttachmentUpload: (state, payload: { key: string, update: ItemAttachmentUploadState }) => {
        Vue.set(state.ongoingItemAttachmentUploads, payload.key, payload.update);
      },
      removeOngoingItemAttachmentUpload: (state, key: string) => {
        Vue.delete(state.ongoingItemAttachmentUploads, key);
      },
      increaseDialogCounter: (state) => { state.dialogCounter++; },
      decreaseDialogCounter: (state) => { state.dialogCounter--; },
      setCompactColumnSizes: (state, payload: { sizes: Record<string, number>, key: string }) => {
        if (!state.userInterfaceSettings.compactColumnSizesMap) {
          Vue.set(state.userInterfaceSettings, 'compactColumnSizesMap', {});
        }
        Vue.set(state.userInterfaceSettings.compactColumnSizesMap!, payload.key, payload.sizes);
      },
      setPosterFrameProcessingState: (state, payload: PosterFrameProcessingType) => state.posterFrameProcessingState = payload,
      toggleExperimental: (state) => {
        state.experimental = !state.experimental;
      },
      copiedItemId: (state, val: string) => state.copiedItemId = val,
      setItemEditStatus: (state, payload: SetItemEditStatusMutationPayload) => {
        Vue.set(state.itemEditStatusMap, payload.itemId, payload.statuses);
      },
      playoutDevicesHelper: (state, payload: PlayoutDevicesHelper) => state.playoutDevicesHelper = payload,
      patchItemDtos: mutations.patchItemDtosMutation,
      patchItemProgressInfo: mutations.patchItemProgressInfo,
      enableUpdatingMimir: (state, val: boolean) => state.enableUpdatingMimir = val,
      setCopyLinkItemId(state, payload: string) {
        state.lastCopyLinkItemId = payload;
      },
      setStoragesLoaded(state, payload: boolean) {
        state.storagesLoaded = payload;
      },
    },

    getters: {
      isFolderTreeActive: state => state.userInterfaceSettings.isFolderTreeActive ?? true,
      useRichPayloadsForPostmessage: state => state.enableRichPayloads,
      compactColumnSizesMap: state => state.userInterfaceSettings.compactColumnSizesMap ?? {},
      thirdSearchColumn: state => state.userInterfaceSettings.thirdColumn ?? null,
      bookmarkMenuOpened: state => !!state.userInterfaceSettings.bookmarkMenuOpened,
      bookmarksOpened: state => state.userInterfaceSettings.bookmarksOpened ?? [],
      itemDeduplicationEnabled: (state) => {
        return state.config.tenantConfig.featureFlags &&
               state.config.tenantConfig.featureFlags['itemDeduplication'] === true;
      },
      allUsers: state => state.allUsers,
      allUsersById: state => keyBy(state.allUsers, u => u.id),
      userById: state => (userId: string) => state.allUsers.find(user => user.id === userId),
      thisClientInstanceId: state => state.thisClientInstanceId,
      searchMenuExpandState: state => state.userInterfaceSettings.searchMenuExpandState ?? true,
      showPlayoutDevices: state => state.userInterfaceSettings.showPlayoutDevices ?? false,
      wsConnected: state => state.wsConnected,
      getMosItemReplaceData: state => state.mosItemReplaceData,
      postMessageOrigin: state => state.postMessageOrigin,
      currentStickyFolder: state => {
        const folderIdFilter = state.filters.defaultFilters['filter:folderId'];
        return folderIdFilter.getStickiedFolderId();
      },
      includeSubfolders: state => state.userInterfaceSettings.includeSubfolders ?? false,
      drawerVisible: state => state.userInterfaceSettings.drawerVisible ?? Vue.prototype.$vuetify?.breakpoint.mdAndUp,
      drawerWidth: state => state.userInterfaceSettings.drawerWidth ?? 230,
      preventBrowserNavigationWarning: state => state.preventBrowserNavigationWarning,
      loadedUploadLocation: state => state.loadedUploadLocation,
      isBeingPolled: state => (itemId: string) => state.itemIdsToPoll.includes(itemId),
      allItemsToPoll: state => state.itemIdsToPoll,
      uploadSummary: state => state.uploadSummary,
      shouldCondenseUpload: state => state.ongoingUploads.length > UploadItem.MAX_CONCURRENT_VISIBLE_ITEMS,
      getExpectedItemType: state => state.expectedItemType,
      selectedFiles: state => state.selectedFiles,
      getUserInterfaceSettings: state => state.userInterfaceSettings,
      appLoaded: state => state.appLoaded,
      filters: state => state.filters,
      dateFilter: state => state.filters.dateFilter,
      geoShapeFilter: state => state.filters.defaultFilters['filter:geoLocation'],
      typeFilter: state => state.filters.defaultFilters['filter:itemType'],
      searchMapZoom: state => state.searchMap.zoom,
      searchMapCenter: state => state.searchMap.center,
      searchMapHasInitialState: state => state.searchMap.isInitialMap,
      allFilters: state => combineFilters(state.filters),
      filtersWithValue: (state, getters) => {
        // TODO: Filter is a pure ts class, and depending on
        // reactive updates on filter.hasValue here seems sketchy.
        return (getters.allFilters as Filter<any>[]).filter(filter => filter.hasValue && !isDefault(filter, state.filters.defaultFilters));
      },
      shouldShowDateFilter: (state) => state.userInterfaceSettings.showDateFilter ?? true,
      shouldShowSameTime: (_state, getters) => {
        const filtersWithValue = getters.filtersWithValue as Filter<any>[];
        let filterCount = 0;
        for (const f of filtersWithValue) {
          const filterType = filterTypeForKey(f.key);
          if (supportsSameTime(filterType)) {
            filterCount += f.filterValues.length;
            if (filterCount > 1)
              return true;
          }
        }
        return false;
      },
      ongoingUploads: state => state.ongoingUploads,
      selectedUpload: state => state.selectedUpload,
      user: state => state.user,
      tenantLabel: state => {
        if (state.config.tenantConfig.organizationLabel)
          return state.config.tenantConfig.organizationLabel;
        if (state.user.tenantId === 'mediability') {
          return 'Fonn Group';
        }
        return startCase(state.user.tenantId);
      },
      clientConfig: _state => getGlobalServiceDocument().clientConfig,
      storages: state => sortBy(state.storage.all, 'name') as StorageType[],
      liveSources: state => state.liveSources,
      routers: state => state.routers,
      optionLists: state => state.optionLists,
      optionListsById: state => (optionListId: string) => state.optionLists.find(optionList => optionList.id === optionListId),
      getStorageByIdFromState: state => (storageId: string) => {
        return state.storage.all.find((storage: StorageType) => storage.id === storageId);
      },
      defaultStorage: state => state.storage.default,
      isSuperAdmin: state => state.user.roles.superAdmin,
      isTenantAdmin: state => state.user.roles.tenantAdmin || (state.user.permissions ? hasTenantInfoAccess(state.user.permissions) : false),
      hasTenantInfoAccess: ({ user }) => user.permissions ? hasTenantInfoAccess(user.permissions) : false,
      canPerformOwnTenantSupport: ({ user }) => user.permissions ? canPerformOwnTenantSupport(user.permissions) : false,
      itemUploadLock: state => state.itemUploadLock,
      getDefaultAnalyticConfig: state => (itemType: DynamoItemType) => getAnalyticConfig(itemType, state.config.tenantConfig.analyticConfig!),
      getConformDefaultAnalyticConfig: state => (itemType: DynamoItemType) => getAnalyticConfig(itemType, state.config.tenantConfig.conformAnalyticConfig!),
      getRecordingsDefaultAnalyticConfig: state => cloneDeep(state.config.tenantConfig.recordingsAnalyticConfig!),
      getColdStorageConfig: state => state.config.tenantConfig.archiveConfig,
      uploadStorageKey: (state): string  | null => {
        const loc = state.loadedUploadLocation;
        if (loc?.type == null)
          return null;

        const bucketScope = loc.type == 's3Bucket' ? 'global' : state.user.tenantId;
        return loc.type + ':' + bucketScope + ':' + loc.name;
      },
      /*
        Get the local path that is equivalent to the configured default upload
        location. May return null if action 'prepareUploadLocation' has not been
        dispatched and awaited yet. Will also return null if no local equivalent
        has been configured.
      */
      localUploadPath: (state, getters): (string | null) => {
        const storageKey: string | null = getters.uploadStorageKey;
        if (storageKey == null)
          return null;

        const localStorageSettings = state.desktop.config.localStorageSettingsByStorageKey ?? {};
        return localStorageSettings[storageKey]?.localFsPath ?? null;
      },
      preferredLocalUploadPath: (state, _getters) => {
        const storageKey: string | null = state.preferredUploadStorageKey;
        if (!storageKey) {
          return storageKey;
        }

        const localStorageSettings = state.desktop.config.localStorageSettingsByStorageKey ?? {};
        return localStorageSettings[storageKey]?.localFsPath ?? null;
      },
      desktopProfileMenuName: state => state.desktopProfileMenuName,
      isMimirBrowse: state => state.layoutFeatures.browse ?? false,
      ongoingItemAttachmentUploads: state => state.ongoingItemAttachmentUploads,
      isSomeDialogVisible: state => state.dialogCounter !== 0,
      posterFrameProcessingState: state => state.posterFrameProcessingState,
      app: _ => appState,
      copiedItemId: state => state.copiedItemId,
      getOptionListByID: state => (id: string) => {
        const optionLists = state.optionLists as OptionListDto[];
        return optionLists.find(l => l.id === id)?.alternatives ?? [];
      },

      itemEditStatusMap: state => state.itemEditStatusMap,
      playoutDevicesHelper: state => state.playoutDevicesHelper,
      isUserDeactivated: state => state.user.isDeactivated,
      enableUpdatingMimir: state => state.enableUpdatingMimir,
      copiedLinkItemId: state => state.lastCopyLinkItemId,
      storagesLoaded: state => state.storagesLoaded,
    },
    actions: {
      initializeSortParams: async (context) => {
        const params = loadSortParams();
        await context.dispatch('setSortParamsInternal', { params });
      },
      setSortParamsInternal: async (context, payload: { params: SortParameters, doNotChangeLevel?: boolean }) => {
        context.commit('setSearchFormSortParams', payload);
      },
      setSortParams: async (context, payload: { params: SortParameters, doNotChangeLevel?: boolean }) => {
        const { params } = payload;
        await context.dispatch('setSortParamsInternal', payload);
        if ((context.getters.itemSearch as SearchState).viewType !== ViewType.COMPACT) {
          saveSortParams(params);
        }
      },
      setSortOrder: async (context, order: string) => {
        await context.dispatch('setSortParams', { params: { ...context.getters.sortBy, order } });
      },
      openFolder: (_context, folderId: string) => {
        if (isValidGUID(folderId)) {
          goToFolder({ id : folderId });
        } else {
          console.warn('Invalid folderID received. ID can only include alphanumeric characters and dashes. Received:', folderId);
        }
      },
      openItem: (context, payload: OpenItemPayload) => {
        const { item, routerLink, replaceHistory, quickPreview } = payload;

        // In quick-preview if user has unsaved changes then setting setSearchItem will erase the changes.
        // To show the unsaved dialog we should not commit setSearchItem here.
        const hasUnsavedChanges = context.getters.userMadeMetadataChanges as boolean;
        if (!hasUnsavedChanges) {
          /**
          * Even when opening the item in the item page its important to commit the current
          * item to the store, so the item page can load incrementally using that object.
          */
          quickPreview.ops.setSearchItem({ item });
        }

        const routerLink_ = routerLink ?? item.routerLink ?? generateRouterLink(item.id, context.getters.lastSearchParams, item.itemType);

        const router = getGlobalRouter();
        if (replaceHistory) {
          void router.replace(routerLink_).catch(noop);
        } else {
          void router.push(routerLink_).catch(noop);
        }
      },
      openItemById: (
        context,
        payload: OpenItemByIdPayload
      ) => {
        const { applicationContext, itemId, returnTo, time, quickPreviewMediaPlayerOps, task, taskTab, taskFilterUser, taskFilterResolved, quickPreview } = payload;
        if (isValidGUID(itemId)) {
          // In mimir browse we do the single item search instead of moving the user to the item details page.
          if (context.getters.isMimirBrowse) {
            storeDispatchSingleItemSearch({
              applicationContext,
              itemId,
              quickPreview,
              quickPreviewMediaPlayerOps,
            }).catch(err => console.error('openItemById action Ex', err));
          } else {
            void getGlobalRouter().push({
              name: 'itemDetails',
              params: {
                id: itemId.trim().toLowerCase()
              },
              query: {
                ...(returnTo ? { returnTo } : {}),
                ...(task ? { task } : {}),
                ...(taskTab ? { taskTab } : {}),
                ...(taskFilterUser ? { taskFilterUser } : {}),
                ...(taskFilterResolved ? { taskFilterResolved } : {}),
                time: time?.toString(),
                tab: payload.tab,
              }
            }).catch(noop);
          }
        } else {
          console.warn('Invalid itemID received. ID can only include alphanumeric characters and dashes. Received:', itemId);
        }
      },
      fetchVersionsForSearch: async (context, payload: FetchVersionsForSearchPayload) => {
        const { itemId, quickPreviewMediaPlayerOps, quickPreview } = payload;
        if (isValidGUID(itemId)) {
          context.commit('resetSearch');
          context.commit('setIsLoadingSearch', true);
          context.commit('clearSelectedSearchItems');
          try {
            const versionsHistoryDto = await getItemVersionsHistory(itemId, true);
            versionsHistoryDto.versions[0].isLatestVersion = true;
            const itemVersionsHistorySortedByDateDesc = getVersionsSortedByCreatedDateDesc(versionsHistoryDto.versions);
            const versionsToInject = itemVersionsHistorySortedByDateDesc.map((v, i) => {
              const versionName = v.name || appState.dateHelper.date(v.versionCreatedOn).format('D MMMM, hh:mm A');
              v.versionName = versionName + ' (v' + (versionsHistoryDto.versions.length - i) + ')';
              return v;
            });
            context.commit('injectItemsIntoSearchResult', { items: versionsToInject.filter(v => v.versionState !== VersionState.DELETED), ignoreSorting: true });
            const searchPlayerItemId = quickPreview.state.currentItem?.id;
            const versionToShowSelected = versionsToInject.find(s => s.id === (searchPlayerItemId ?? itemId) && s.versionState !== VersionState.DELETED);
            if (versionToShowSelected) {
              // We have to fetch a full item here since versions are just a RelatedItem payload
              // with a few additional fields. This payload does not have the sufficient fields
              // in order to work correctly in the quick preview player
              const item = await getItem(versionToShowSelected.id);
              if (!searchPlayerItemId) void quickPreview.ops.loadItemInQuickPreview({ item, quickPreviewMediaPlayerOps });
              context.commit('addToSelectedSearchItems', versionToShowSelected);
            }
          } catch (err: any) {
            if (err?.response?.data?.error?.statusCode === 404) {
              context.commit('setCustomNoResultMessage', `Item with id '${itemId}' could not be found.`);
            } else {
              logErrorAndShowToast('Could not get item version history', err);
            }
          } finally {
            context.commit('setIsLoadingSearch', false);
          }
        }
      },
      removeFromOngoingUploads: (context, item: UploadItem) => {
        context.commit('removeFromOngoingUploads', item);
        // If the item thats being removed is selected, select another one if present.
        if (item === context.state.selectedUpload) {
          if (context.state.ongoingUploads.length > 0)
            context.commit('setSelectedUpload', context.state.ongoingUploads[0]);
          else
            context.commit('setSelectedUpload', null);
        }
      },
      finishAndClearUpload: ({ commit }, payload: UploadSummary) => {
        commit('setUploadSummary', payload);
        commit('clearAllUploadsExceptErrored');
      },

      /*
        Upload a collection of standalone root items.

        If you intend to upload a single root item consisting of multiple files, use 'uploadItemFiles' instead.
      */
      doUpload: async (context, payload: StoreDoUploadPayload) => {
        const { uploadManager, applicationContext, items, inject, isProxyMode, quickPreview } = payload;

        const MAX_INJECT_SIZE = 100;
        context.commit('setUploadSummary', null);
        for (const uploadItem of items) {
          context.commit('pushToOngoingUploads', uploadItem);

          if (inject && uploadItem.folderParents !== null && !uploadItem.isHidden && items.length <= MAX_INJECT_SIZE) {
            storeDispatchInsertUploadItemIntoSearch({
              applicationContext,
              uploadItem,
              isProxyMode,
              addRouterLink: false,
              quickPreview,
            });
          }
        }

        // Delegate to upload manager:
        await combineAll(items.map(async item => {
          /*
            Each individual item upload gets their own cancellation token, so that
            they can be cancelled individually.
          */
          const cts = Axios.CancelToken.source();
          const cancel: Canceler = cts.cancel;

          // Store the local ID before upload (for Sentry tracking)
          const localId = item.id;

          // Track if we've already updated the Sentry ID mapping for this upload
          let sentryIdUpdated = false;

          const listener: FileSetListener = (progress: FileSetProgress) => {
            appState.putUploadProgress(item.id, { progress, cancel });

            // Check if the item ID has changed (meaning the real GUID is now available)
            // and we haven't already updated the Sentry tracking
            if (!sentryIdUpdated && localId !== item.id && !item.isExistingItem && item.id.includes('-')) {
              updateUploadItemId(localId, item.id);
              sentryIdUpdated = true;
            }
          };
          invariant(isFileApi(item.file), 'Upload item must have file');
          const fileItem = item as typeof item & { file: FileApi };

          await runTask(
            {
              cancelToken: cts.token,
              // We've hooked up feedback for transient errors, so we can afford to retry forever
              resilience: new ExponentialBackoff({ timeLimit: Infinity })
            },
            (tc) => uploadManager.uploadFileBatchForItem(tc, () => item.permanentId, [fileItem], listener)
          );

          // After upload, update Sentry tracking with real Mimir GUID if item was created
          if (localId !== item.id && !item.isExistingItem) {
            updateUploadItemId(localId, item.id);
          }

          // Since the upload manager's trackUploadComplete call never executes due to hanging,
          // we need to clean up the Sentry tracking here when we know the upload is done
          trackUploadComplete(localId, fileItem.file.size);

          // Once complete, start polling this item.
          context.commit('addItemsToPoll', [item.id]);
        }));

      },
      /*
        Upload all the files related to a root item. In case of stitching, there
        will be multiple fileItems (sub-items) corresponding, to the itemId. For
        other types of item upload there will be a single file item given.

        If you intend to upload a whole folder as many independent root items,
        rather than stitching all of the files into a single item, then please use
        'storeDispatchDoUpload' instead.

        itemId: The itemId of the root item to file progress information under.

        fileItems: The files to upload for this root item.

        searchItem: Item to inject into the search page.
      */
      uploadItemFiles: async (context, payload: UploadItemFilesPayload) => {

        // Lock root item for upload. We do this to ensure other clients get notified via websocket that this item is currently worth polling
        try {
          const lockRequest: CreateItemUploadLockRequestDto = {
            lockOwnerInstanceId: context.getters.thisClientInstanceId,
          };
          await createItemUploadLock(payload.itemId, lockRequest);
        } catch (err) {
          // Do not fail the full operation when this call fails as it's not a critical error
          console.error('Error occurred in obtaining upload lock for P2 root item, ignoring.', err);
        }

        for (const fileItem of payload.fileItems) {
          context.commit('pushToOngoingUploads', fileItem);
        }

        const uploadItem = payload.searchItem;
        if (uploadItem?.folderParents != null) {
          storeDispatchInsertUploadItemIntoSearch({
            applicationContext: payload.applicationContext,
            uploadItem,
            isProxyMode: payload.isProxyMode,
            addRouterLink: false,
            quickPreview: payload.quickPreview,
          });
        }

        /*
          A single cancellation token for the whole item, which will be used for
          upload of all the individual files to be uploaded for the item.
        */
        const cts = Axios.CancelToken.source();
        const cancel: Canceler = cts.cancel;

        // Delegate to upload manager:
        const listener: FileSetListener = (progress: FileSetProgress) => {
          appState.putUploadProgress(payload.itemId, { progress, cancel });
        };

        await runTask(
          {
            cancelToken: cts.token,
              // We've hooked up feedback for transient errors, so we can afford to retry forever
              resilience: new ExponentialBackoff({ timeLimit: Infinity })
          },
          (ct) => payload.uploadManager.uploadFileBatchForItem(ct, () => payload.itemId, payload.fileItems, listener)
        );

        // Once complete, start polling this item.
        context.commit('addItemsToPoll', [payload.itemId]);
      },
      clearAllFilters: context => {
        context.commit('clearAllFilters');
        resetDateFilterIfCustom(context.state.filters, context.dispatch);
        EventBus.emit(EVENTS.RESET_DATEFILTER);
      },
      clearCustom: context => {
        context.commit('unsetGlobalMdf');
        context.commit('clearCustom');
        resetDateFilterIfCustom(context.state.filters, context.dispatch);
      },
      updateItemsFromPoll: async (
        context,
        payload: {
          items: StandardItemEntryDto[],
          retrievedFields: (keyof LoadedItem)[],
          quickPreview: QuickPreview,
        }
      ) => {
        /*
          Compare existing item DTOs against the ones we've just fetched to
          identify item state changes which indicate that our current media links
          to things like thumbnails may be outdated.
        */

        const outdatedMediaItems: StandardItemEntryDto[] = mutations.getOutdatedMediaItems(context.state, context.state.lastPolledItems, payload.items);

        context.state.lastPolledItems = [...payload.items];

        /*
          Patch existing item DTOs, selectively applying fetched state.
        */
        context.commit('patchItemDtos', payload);

        /*
          Fetch additional state from items we considered have potentially
          outdated media links.
        */
        for (const hit of outdatedMediaItems) {
          await storeDispatchUpdateSearchHitWithMedia({ update: hit, quickPreview: payload.quickPreview });
        }
      },
      updatePersonInResultSet: (context, person: PersonResource) => context.commit('updatePersonInResultSet', person),
      setIsVideoPlaying: (context, setIsVideoPlaying) => context.commit('setIsVideoPlaying', setIsVideoPlaying),
      setIsScrubbingVideo: (context, setIsScrubbingVideo) => context.commit('setIsScrubbingVideo', setIsScrubbingVideo),
      updateStorage: (context, payload: { updatedStorage: StorageType; storageId: string }) => context.commit('updateStorage', payload),
      updateLiveSource: (context, payload: LiveSourceDtoV2) => context.commit('updateLiveSource', payload),
      /*
        TODO: This store actions does not have any asynchronous operations, and
        its responsibilities can be shifted to the 'defaultStorage' mutation
        instead. At the same time we should consider changing the synching
        strategy for keeping the local storage state in sync with the store to use
        a store state watcher instead. Using watchers have less edge cases that
        modification guards like this, and would work fine when doing time-travel
        in the Vue browser extension development tools. See:
        https://vuex.vuejs.org/api/#watch
      */
      setDefaultStorage: (context, value: StorageType | undefined) => {
        context.commit('defaultStorage', value);
        setDefaultStorage(value?.id);
      },
      undeleteItem: async (context, itemId: string) => {
        try {
          context.commit('removeIdsFromSearchResult', { idsToRemove: [itemId]});
          await undeleteItem(itemId);
          showToast(getI18n().t('IndexTS.ItemRestored'), TOAST.SUCCESS);
        } catch (ex) {
          const err = ex as any;
          const msg = 'Failed to restore the item.' + (err?.response?.status === 404 ? 'It has probably been deleted.' : '');
          logErrorAndShowToast(msg, err, 'ex in undeleteItem');
          EventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
        }
      },
      doBatchRestore: async (context, searchItems: SearchResultHit[]) => {
        if (searchItems.length === 0) return;

        const persons = searchItems.filter(item => item.itemType === ItemType.PERSON);
        const items = searchItems.filter(item => item.itemType !== ItemType.PERSON);
        const onlyOnePerson = persons.length === 1;
        const includesPersons = persons.length > 0;

        const onlyOneItem = items.length === 1;
        const includesItems = items.length > 0;

        const messageParts = [
          includesItems ? `${items.length} item${onlyOneItem ? '' : 's'}` : '',
          includesPersons ? `${persons.length} person${onlyOnePerson ? '' : 's'}` : '',
        ].filter(s => s); // drop empty strings

        let messageBuilder = new SafeHtmlBuilder();
        const header = 'Are you sure?';

        if (messageParts.length === 2) {
          const [x, y] = messageParts;
          messageBuilder = new SafeHtmlBuilder().addText(`This will restore ${x} and ${y}.`);
        } else {
          messageBuilder = new SafeHtmlBuilder().addText(`This will restore ${messageParts[0]}.`);
        }

        if (includesPersons) {
          messageBuilder.addLineBreakAndAppendText('')
            .addLineBreakAndAppendText(' Restoring person(s) will also restore deleted images that were uploaded as part of detecting this person.');
        }

        void showSimpleAsyncDialog({
          header,
          baseHtml: messageBuilder,
          maxWidth: 450,
          icon: 'announcement',
          actionText: 'Restore',
          closeOnActionClick: true,
        },
        async () => {
          // For more than 5 items we show a please wait dialog for the requests to process.
          if (searchItems.length >= 5) {
            showPleaseWaitDialog('Restoring ..');
          }

          // Filtering out person-images if their corresponding person is in the selected list since restoring those is
          // taken care of by restoring the person itself.
          const itemsThatShouldBeIgnored = filterOutMatchingPersonImages(searchItems) || {};

          // Optimistically remove everywhere in client.
          const promises = [];
          const itemsToRemoveFromSearchView = searchItems.map(item => item.id);
          for (const item of searchItems) {

            // If we're restoring a person, but the user did not select the images belonging to this person, ensure we are
            // optimistically removing those from the view as well, as the restore operation will restore those too.
            if (isInstanceOfPersonDto(item) && item.imageIds && item.imageIds.length > 0) {
              for (const imageId of item.imageIds) {
                if (!itemsToRemoveFromSearchView.includes(imageId)) {
                  itemsToRemoveFromSearchView.push(imageId);
                }
              }
            }

            if (!itemsThatShouldBeIgnored[item.id]) {
              continue;
            }

            promises.push(undeleteItem(item.id).catch(err => ({ err, status: err?.response?.status })));
          }

          context.commit('removeIdsFromSearchResult', { idsToRemove: itemsToRemoveFromSearchView });
          context.commit('clearSelectedSearchItems');

          const results = await Promise.all(promises);
          const failures = results.filter(x => !!x.err);

          if (failures.length > 0) {
            if (failures.every(x => x.status === 404)) {
              logErrorAndShowToast('Failed to restore some items as they have been deleted meanwhile.', failures);
            } else {
              // Revert state from server call on error
              EventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
              logErrorAndShowToast('Error occurred when trying to restore. Please try again later.', failures);
            }
          }

          if (searchItems.length >= 5) hidePleaseWaitDialog();
        });
      },
      undeletePerson: async (context, person: Pick<PersonDto, 'id' | 'imageIds'>) => {
        context.commit('removeIdsFromSearchResult', { idsToRemove: [person.id, ... person.imageIds || []]});
        try {
          await undeleteItem(person.id);
          showToast('Person restored', TOAST.SUCCESS);
          return true;
        } catch (error) {
          logErrorAndShowToast('Failed to restore the person', error, 'ex in undeletePerson');
          EventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
        }
        return false;
      },
      cacheUserRoles: async context => {
        try {
          const [superAdmin, tenantAdmin, supportTeam] = await Promise.all([
            isSuperAdmin(),
            isTenantAdmin(),
            isSupportTeam(),
          ]);
          const userRole: UserRole = { superAdmin, tenantAdmin, supportTeam };
          context.commit('setUserRoles', userRole);
        } catch(err) {
          logErrorAndShowToast('Error occurred while fetching user roles.', err, 'ex in cacheUserRoles');
        }
      },
      getTenantStorages: async (context, payload: {
        applicationContext: ApplicationContext,
        ongoingUploads: UploadItem[],
        downloadPath: string,
        localStateOps: LocalStateOps | undefined,
      }) => {
        const { applicationContext, ongoingUploads, downloadPath, localStateOps } = payload;
        try {
          const storageInfos = await Promise.all([
            getTenantStorages(),
            context.dispatch('loadKeldaConfigs'),
            getKeldaStatuses(),
          ]);

          const storages = getEmbed(storageInfos[0], 'collection', []) as StorageType[];

          const keldaConfigs = context.getters.keldaConfigs as KeldaConfigDto[];
          const allKeldaStatuses = storageInfos[2];
          const keldaStorages: StorageType[] = [];
          for (const kConfig of keldaConfigs) {
            const { storageFolders } = kConfig;
            if (!storageFolders) continue;
            const keldaStatus = allKeldaStatuses.find(k => k.configId === kConfig.id);
            const storagesForKelda = getActiveStorageFolders(kConfig, keldaStatus, context.state.user.tenantId);
            keldaStorages.push(...storagesForKelda);
          }

          context.commit('storages', [...storages, ...keldaStorages]);
          const defaultStorageId = getDefaultStorage() || '';
          const defaultStorage = getStorageByIdFromState(defaultStorageId);

          await context.dispatch('setDefaultStorage', defaultStorage);
          if (defaultStorage) {
            const notificationOps = new DefaultNotificationOps(applicationContext);
            const services = {
              applicationContext,
              notificationOps,
              logger: new ConsoleLogger(),
              ongoingDownloadOps: new DefaultOngoingDownloadOps(),
              localStateOps,
            };
            let userDataSystemPath: string | undefined;
            if (isInPremiere(applicationContext) || isInElectron(applicationContext)) {
              userDataSystemPath = getUserDataPath(applicationContext);
            }
            const config = {
              downloadPath,
              userDataSystemPath,
              shouldUsePremierePreferences: context.rootGetters.usePremierePreferenceForScaleToFrame,
              skippedFileExtensions: (context.rootGetters.tenantConfig as TenantConfigDto).skippedFileExtensionsForPremiere,
            };
            startPollingTransferProgress(services, config, ongoingUploads, defaultStorage.id);
          }
        } catch(error) {
          logErrorAndShowToast('Error occurred when loading storages, please try again later.', error, 'ex in getTenantStorages');
        } finally {
          context.commit('setStoragesLoaded', true);
        }
      },

      getLiveSources: async context => {
        try {
          const liveSources: LiveSourceDtoV2[] = await getLiveSourcesV2();
          context.commit('liveSources', liveSources);
        } catch(error) {
          logErrorAndShowToast('Error occurred when fetching live sources, please try again later.', error, 'ex in getLiveSources');
        }
      },

      getRouters: async context => {
        try {
          const routers: RouterDto[] = await getRouters();
          context.commit('routers', routers);
        } catch(error) {
          logErrorAndShowToast('Error occurred when fetching routers, please try again later.', error, 'ex in getRouters');
        }
      },

      getOptionLists: async context => {
        try {
          const optionListsIdsObj = await getOptionListIds();
          const optionLists: OptionListDto[] = await Promise.all(optionListsIdsObj.ids.map(getOptionList));
          context.commit('optionLists', optionLists);
        } catch(error) {
          logErrorAndShowToast('Error occurred when fetching option lists, please try again later.', error, 'ex in getOptionLists');
        }
      },

      getOptionListsIfNotAlready: async (context, ids: string[]) => {
        const existingListsMap: Record<string, OptionListDto | undefined> = {};
        for (const ol of context.state.optionLists) {
          existingListsMap[ol.id] = ol;
        }
        const uniqueIds = [...new Set(ids)];
        const promises: Promise<OptionListDto>[] = [];
        for (const id of uniqueIds) {
          if (existingListsMap[id] || context.state.ongoingFetchOfOptionListIds[id]) continue;
          context.state.ongoingFetchOfOptionListIds[id] = true;
          promises.push(getOptionList(id).finally(() => {
            context.state.ongoingFetchOfOptionListIds[id] = false;
          }));
        }

        const list = await Promise.allSettled(promises);
        for (const l of list) {
          if (l.status === 'fulfilled') context.commit('addOptionList', l.value);
        }
      },
      fetchOptionListsForTimedMetadataIfNeeded: async (context) => {
        const schemas = context.rootGetters.timedMdfs as MetaDataSchema[];
        const timedMdfsfInItem = context.rootGetters['timedMdf/itemMdfCollectionSorted'] as TimedMetadataItemModel[];

        const usedSchemaIds = new Set(timedMdfsfInItem.map(tmdf => tmdf.data.formId));
        const fields = schemas.filter(schema => usedSchemaIds.has(schema.id)).flatMap(schema => schema.fields);
        const fetchedOptionListsById = keyBy(context.getters.optionLists as OptionListDto[], 'id');
        const optionListIds: string[] = [];
        fields.forEach(field => {
          if (field.optionListId && !fetchedOptionListsById[field.optionListId]) {
            optionListIds.push(field.optionListId);
          }
        });
        if (optionListIds.length) await context.dispatch('getOptionListsIfNotAlready', optionListIds);
      },
      toggleSearchItemSelection: async (context, payload: ToggleSearchItemSelectionPayload) => {
        const { isMultiSelectionMode, quickPreviewMediaPlayerOps, timeToSeekInMs, quickPreview } = payload;
        // As compact view transforms to fieldId, hence getting the item from search result
        const currentState = context.getters.itemSearch as SearchState;
        const item = currentState.viewType === ViewType.COMPACT ? currentState.results.hits.find(hit => hit.id === payload.item?.id) : payload.item;
        invariant(item, 'Expected selected item to be present in search result state!');
        if (!isMultiSelectionMode) {
          //By checking selected property here we can eliminate multiple click issue on a selected item.
          if (item?.selected && context.getters.selectedSearchItems?.length <= 1) return;

          if (context.getters.userMadeMetadataChanges) {
            // If previous item has unsaved changes in quick-preview we should show dialog before selecting another item.
            appState.setItemToBePreviewed(item);
            return;
          }

          const showFolderMetadata = context.getters.featureFlags.showFolderMetadataEditor as boolean;
          if (showFolderMetadata && item.itemType !== ItemType.FOLDER && folders.currentFolder) {
            setFolderIdToPreview(folders.currentFolder.id);
          }

          context.commit('clearSelectedSearchItems');
          context.commit('addToSelectedSearchItems', item);
          if (item.itemType === ItemType.FOLDER) {
            quickPreview.ops.setSearchItem({ item: null });
            return setFolderIdToPreview(item.id);
          }
          const autoPlayFrom = getInitialPlayStartTime(item);
          return quickPreview.ops.loadItemInQuickPreview({ item, autoPlayFrom, quickPreviewMediaPlayerOps, timeToSeekInMs });
        }

        const toSelect = !context.getters.isSelectedSearchItem(item);
        if (toSelect) {
          context.commit('addToSelectedSearchItems', item);
          if (item.itemType === ItemType.FOLDER) {
            return setFolderIdToPreview(item.id);
          }
          return quickPreview.ops.loadItemInQuickPreview({ item, quickPreviewMediaPlayerOps });
        }

        context.commit('removeFromSelectedSearchItems', item);
        const currentItemId = quickPreview.state.currentItem?.id;
        if (currentItemId !== item.id) return;

        const nextItem = (context.getters.selectedSearchItems as SearchResultHit[])
          .filter(selectedItem => selectedItem.id !== item.id)
          .pop();

        if (nextItem) {
          if (item.itemType === ItemType.FOLDER) {
            return setFolderIdToPreview(nextItem.id);
          }
          return quickPreview.ops.loadItemInQuickPreview({ item: nextItem, quickPreviewMediaPlayerOps });
        }

        quickPreview.ops.setSearchItem({ item: null });
      },
      toggleAllSearchItemsSelection: (context) => {
        const currentState = context.getters.itemSearch as SearchState;
        const allItems = currentState.results.hits;

        if (allItems.length === 0) return;

        context.commit('clearSelectedSearchItems');
        for (const item of allItems) {
          context.commit('addToSelectedSearchItems', item);
        }
      },
      getItemUploadLockInfo: async (context, payload: string) => {
        if (payload) {
          try {
            const itemUpload = await getItemUploadLock(payload);
            context.commit('setItemUploadLock', itemUpload);
            return itemUpload;
          } catch (err) {
            logErrorAndShowToast('Unable to obtain item upload lock information.', err, 'ex in getItemUploadLockInfo');
          }
        }
      },
      deleteItemUploadLock: async (context, payload: string) => {
        await deleteItemUploadLock(payload);
        return context.dispatch('getItemUploadLockInfo', payload);
      },
      consecutiveSearchItemSelection: async (context, payload: ConsecutiveSearchItemSelectionPayload) => {
        const { item, quickPreviewMediaPlayerOps, timeToSeekInMs, quickPreview } = payload;

        const itemSearch: SearchState = context.getters.itemSearch;
        const items = itemSearch.results.hits;
        const currentItem = quickPreview.state.currentItem as Item | null;

        let x = items.findIndex(hit => hit.id === item.id);
        let y = currentItem ? items.findIndex(hit => hit.id === currentItem.id) : x;

        if (x > y) [x, y] = [y, x]; // So that, always x < y

        for (let i = x; i <= y; ++i) {
          // During selected items update we need updated metadata of all items to avoid value mismatch.
          context.commit('addToSelectedSearchItems', items[i]);
        }

        return quickPreview.ops.loadItemInQuickPreview({ item, quickPreviewMediaPlayerOps, timeToSeekInMs });
      },
      loadItemMetadata: async (context, payload: LoadItemMetadataPayload): Promise<LoadItemMetadataResult | null> => {
        const {item, isCurrentItem, quickPreview } = payload;
        if (!item || notGettableItemTypes.includes(item.itemType))
          return null;
        const itemInSearchHit = (context.getters.itemSearch as SearchState).results.hits.find(hit => hit.id === item.id);
        quickPreview.ops.setIsItemMetadataLoading({ id: item.id, loading: true });
        const keys: (keyof LoadItemMetadataResult)[] = [
          'id',
          'aaf',
          'hasRelations',
          'highRes',
          'highresDeletion',
          'ingestSourceFullPath',
          'ingestSourceS3Bucket',
          'ingestSourceStorageId',
          'metadata',
          'metadataVersion',
          'technicalMetadata',
          'timecode',
          'pagedProxy',
          'exifTagsUrl',
          'nonScheduledRecording',
          'isArchived',
          'taskEntries',
          'customIcons',
        ];
        try {
          const result : LoadItemMetadataResult = await getItemFields(item.id, keys);

          if (isCurrentItem && quickPreview.state.currentItem !== item) {
            /**
             * TODO: The way we pass "isCurrentItem" together with the currentItem
             * reference is broken: Since we've now done an async call above, the
             * store's currentItem may have changed.
             * Possible fix: Refactor this method to just take an item id.
             */
            console.warn('loadItemMetadata: Broken currentItem handling');
          }
          // updating both currentItem and search hit as they have different reference
          const hitUpdate = cloneDeep(result);
          const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
          const vueDelete = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
          for (const key of keys) {
            if (key === 'metadata') {
              if (isCurrentItem) {
                handleMdfUpdatesToItem(item, result.metadata, result.metadataVersion, vueSet, vueDelete);
              }
              if (itemInSearchHit) {
                handleMdfUpdatesToItem(itemInSearchHit, result.metadata, result.metadataVersion, vueSet, vueDelete);
              }
            } else {
              if (isCurrentItem) {
                Vue.set(item, key, result[key]);
              }
              if (itemInSearchHit) {
                Vue.set(itemInSearchHit, key, hitUpdate[key]);
              }
            }
          }
          return result;
        } finally {
          quickPreview.ops.setIsItemMetadataLoading({ id: item.id, loading: false });
        }
      },
      loadCurrentItemMetadata: async (_context, payload: LoadCurrentItemMetadataPayload): Promise<LoadItemMetadataResult | null> => {
        const { quickPreview } = payload;
        const currentItem = quickPreview.state.currentItem as Item | null;
        if (!currentItem) return null;
        return await storeDispatchLoadItemMetadata({ item: currentItem, isCurrentItem: true, quickPreview });
      },
      prepareUploadLocation: async (context, reload: boolean) => {
        if (context.state.loadedUploadLocation !== null && context.state.freshUploadLocation && !reload) {
          return;
        }

        try {
          const location = await getTenantConfigDefaultUploadLocation();
          context.commit('setLoadedUploadLocation', !isEmpty(location) ? location : null);
        } catch  (err) {
          context.commit('setLoadedUploadLocation', null);
        }
      },
      initializeDesktopFeatures: async (context, payload: {
        applicationContext: ElectronContext,
        ongoingUploads: UploadItem[],
        downloadPath: string,
        localStateOps: LocalStateOps,
      }) => {
        const { applicationContext, ongoingUploads, downloadPath, localStateOps } = payload;
        context.commit('desktopProfileMenuName', getI18n().t('IndexTS.StorageSettings'));
        if (!getDefaultStorage()) {
          await storeDispatchStorageProfileChanged(
            applicationContext,
            downloadPath,
            context.getters.selectedStorageProfile.storageId,
            ongoingUploads,
            localStateOps,
          ).catch(err => console.error(err));
        }
        const services = {
          applicationContext,
          logger: new ConsoleLogger(),
          getDownloadPath: () => getGlobalStore().getters.downloadPath,
        };
        const ameConfig = getGlobalStore().getters.ameConfig as AmeConfig;
        launchAmeWithConfig(services, ameConfig);
        const dvrConfig = getGlobalStore().getters.dvrConfig as DvrConfig;
        launchDvrWithConfig(services, dvrConfig);
      },
      doBatchReprocess: async (context, searchItems: SearchResultHit[]) => {
        if (searchItems.length === 0) return;

        const messageBuilder = new SafeHtmlBuilder().addText(getI18n().t('IndexTS.ReprocessMessage', { count: searchItems.length }));
        const header = getI18n().t('IndexTS.AreYouSure');

        void showSimpleAsyncDialog({
          header,
          baseHtml: messageBuilder,
          maxWidth: 450,
          icon: 'announcement',
          actionText: getI18n().t('IndexTS.Reprocess') || '',
          closeOnActionClick: true,
        },
        async () => {
          try {
            const promises = [];
            for (const searchItem of searchItems) {
              const reprocessActionType: ReprocessActionType = isKeldaLiveItem(searchItem) ? ReprocessActionType.LIVE_ITEM_REINGEST : ReprocessActionType.TRANSCODING_STATE_PROGRESSING;
              const isKeldaItem = searchItem.ingest?.source.scheme === 'kelda';
              promises.push(reprocessItem(searchItem.id, context.state.user.id, reprocessActionType));
              if (reprocessActionType === ReprocessActionType.TRANSCODING_STATE_PROGRESSING) {
                const update = {
                  id: searchItem.id,
                  update: {
                    // When reprocessing a cloud item, the work starts at transcoding
                    // the highres file that is already in the cloud.
                    // However, when reprocessing a Kelda item, other operations
                    // take place like generating a thumbnail and an edit proxy, and it is
                    // only when the edit proxy will be uploaded that we will be in the same
                    // stage of reprocessing as a cloud item, i.e. generating the web proxy and
                    // applying analytics operations. So it makes more sense to reset the item state
                    // to NEW so that it is the upload of the edit proxy that will bring the state back
                    // to PROGRESSING, exactly as it happens the first time the item is ingested
                    itemState: isKeldaItem ? ItemState.NEW : ItemState.PROGRESSING,
                    transcodingState: AsyncOperationState.PROGRESSING,
                  }
                };
                getGlobalStore().commit('updateSearchHitById', update);
              }
            }
            await Promise.all(promises);
          } catch (error) {
            // Revert state from server call on error
            EventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);
            logErrorAndShowToast('Error occurred when trying to reprocess. Please try again later.', error, 'ex in doBatchReprocess');
          }
        });
      },
      doBatchDownload: async (context, searchItems: SearchResultHit[]) => {
        if (searchItems.length === 0) return;
        let browserNavigationWarningPreventionOptionChanged = false;
        try {
          if (!context.getters.preventBrowserNavigationWarning) {
            context.commit('preventBrowserNavigationWarning', true);
            browserNavigationWarningPreventionOptionChanged = true;
          }

          const operationApiContext = new OperationApiContext(globalBackendClient, new ExponentialBackoff());
          for (const searchItem of searchItems) {
            const item = await getItem(searchItem.id);

            const isStreamed = item?.media?.streaming === false && item?.liveRecording?.state === 'resources_deleted';
            if (isStreamed) {
              const itemTitle = getItemSystemDefaultField(item, SystemDefaultField.Title) as string;
              const ctm = new CancelTokenManager();

              const notifier = getDownloadLiveMediaNotifier(item.id, itemTitle, StreamRoleId.LiveRecordHighresTs, ctm);
              const api = new ApiActions(operationApiContext);

              getCloudLiveMediaBlob(
                item,
                StreamRoleId.LiveRecordHighresTs,
                (part, whole) => {
                  const percent = Math.floor(100 * (part + 1) / whole);
                  notifier.progress(percent);
                },
                api,
                ctm,
              )
              .then(blob => notifier.completed(() => {
                downloadWithoutNavigationWarning(blob, `${itemTitle}.ts`);
              }))
              .catch((err) => {
                logErrorAndShowToast('Error occurred when trying to download live media.', err, 'ex in doBatchDownload');
              });
            } else if (item.itemType === ItemType.IMAGE && item.highRes) {
              invariant(item.originalFileName, 'Expected originalFileName for image item is missing');
              if (item.mediaSize && item.mediaSize > MAX_IMAGE_SIZE_FOR_EXIF_EMBEDDING_BYTES) {
                showToast(
                  'Image size is too large! EXIF embedding is skipped, and the image will be downloaded shortly.',
                  TOAST.WARNING
                );
                await download(item.highRes, item.originalFileName);
              } else {
                try {
                  const response = await getEXIFEmbeddedImageURL(
                    item.highRes,
                    item.originalFileName,
                    item.id
                  );
                  if (response && response.hasExifError) {
                    showToast(
                      'EXIF embedding failed! The image will be downloaded without embedded metadata shortly.',
                      TOAST.WARNING
                    );
                  }
                  if (response && response.downloadUrl) {
                    await download(response.downloadUrl, item.originalFileName);
                  } else {
                    await download(item.highRes, item.originalFileName);
                  }
                } catch (error) {
                  await download(item.highRes, item.originalFileName);
                }
              }
            } else {
              // Either not an image or only proxy is available, so use the regular URL.
              const url = doesItemOnlyHaveProxy(item) ? item.proxy! : item.highRes!;
              await download(url, item.originalFileName!);
            }
          }
        } catch (error) {
          logErrorAndShowToast('Error occurred when trying to download high res. Please try again.', error, 'ex in doBatchDownload');
        } finally {
          if (browserNavigationWarningPreventionOptionChanged) {
            // Reverting the changes
            context.commit('preventBrowserNavigationWarning', false);
          }
        }
      },
      addOrUpdateItemAttachmentUpload: ({ commit }, attachmentPayload: ItemAttachmentUploadState) => {
        const { itemId, type, fileName } = attachmentPayload;
        commit('updateOngoingItemAttachmentUpload', { key: getOngoingAttachmentUploadKey(itemId, type, fileName), update: attachmentPayload });
      },
      removeFromOngoingItemAttachmentUploads: ({ commit }, attachmentPayload: ItemAttachmentUploadState) => {
        const { itemId, type, fileName } = attachmentPayload;
        commit('removeOngoingItemAttachmentUpload', getOngoingAttachmentUploadKey(itemId, type, fileName));
      }
    },
  });



  /**
   * This is the cleanest way to ensure we can monitor the hits currently loaded in search.
   * Whenever this object changes, we do a scan to see if there are items we should be polling for.
   */
  store_.watch(state => state.search.itemSearch.results.hits, _hits => {
    if (!getSearchPageVisible()) {
      // Don't update items to poll from search hits if the search page is not visible
      // which can happen if the user moved away while a search request is in flight
      return;
    }
    pollSearchHitsIfNeeded();
  });

  /**
   * When we detect we are not showing the "same time" checkbox due to the user removing filters,
   * we should ensure the filter is set to false as well so consequent searches don't show the same time results.
   */
  store_.watch(() => store_.getters['shouldShowSameTime'], (shouldShow: boolean) => {
    if (!shouldShow && store_.state.search.searchForm.atSameTime === true) {
      store_.commit('setSameTimeFilter', false);
    }
  });

  /**
   * This takes care of keeping userInterfaceSettings synced to local storage.
   */
  store_.watch(state => state.userInterfaceSettings, settings => {
    setUISettings(settings);
  }, { deep: true });

  /**
   * Keep filters in sync with users
   */
  store_.watch(state => state.allUsers, users => {
    store_.state.filters.setUsers(users);
  });


  setGlobalStore(store_);
  return getGlobalStore();
}
